import { useEffect, useState } from "react";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useT } from "@/i18n";

const TX = {
  es: { title: "Vas a salir de Lumi & Spark", sub: "Pide permiso a un adulto antes de continuar.", adult: "Esta herramienta es para mayores de 13. Un adulto debe responder:", go: "Continuar", stay: "Quedarme aquí", wrong: "Respuesta incorrecta" },
  en: { title: "You're leaving Lumi & Spark", sub: "Ask an adult for permission before continuing.", adult: "This tool is for ages 13+. An adult must answer:", go: "Continue", stay: "Stay here", wrong: "Wrong answer" },
};
const rnd = () => 3 + Math.floor(Math.random() * 7);

export default function ExitGuard() {
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const [target, setTarget] = useState(null);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a || a.dataset.noGuard !== undefined) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin === window.location.origin || !/^https?:$/.test(url.protocol)) return;
      e.preventDefault();
      e.stopPropagation();
      const adult = parseInt(a.dataset.age, 10) >= 13;
      setAnswer("");
      setError(false);
      setTarget({ url: url.href, host: url.hostname.replace(/^www\./, ""), q: adult ? [rnd(), rnd()] : null });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const go = () => {
    if (target.q && Number(answer) !== target.q[0] * target.q[1]) { setError(true); return; }
    window.open(target.url, "_blank", "noopener,noreferrer");
    setTarget(null);
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
      <DialogContent className="rounded-[2rem] border-4 border-yellow-300 max-w-md" data-testid="exit-guard-dialog">
        <div className="flex justify-center -mt-2"><img src="/img/lumi.webp" alt="" className="w-20 mascot-wiggle" /></div>
        <DialogTitle className="font-fredoka text-2xl font-bold text-blue-950 text-center">{tx.title}</DialogTitle>
        <DialogDescription className="text-center font-semibold text-blue-900">{tx.sub}</DialogDescription>
        <p className="text-center text-sm font-bold text-blue-700 inline-flex items-center justify-center gap-1" data-testid="exit-guard-host"><ExternalLink className="w-4 h-4" /> {target?.host}</p>
        {target?.q && (
          <div className="bg-rose-50 rounded-2xl p-4 space-y-2" data-testid="exit-guard-adult">
            <p className="font-bold text-rose-800 text-sm inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> {tx.adult}</p>
            <div className="flex items-center gap-2">
              <span className="font-fredoka text-2xl font-bold text-blue-950" data-testid="exit-guard-question">{target.q[0]} × {target.q[1]} =</span>
              <input value={answer} onChange={(e) => { setAnswer(e.target.value.replace(/\D/g, "").slice(0, 3)); setError(false); }} inputMode="numeric" className="w-20 border-4 border-sky-100 rounded-xl px-3 py-1.5 font-bold text-lg focus:outline-none focus:border-sky-300" data-testid="exit-guard-answer" />
            </div>
            {error && <p className="text-rose-700 text-sm font-bold" data-testid="exit-guard-error">{tx.wrong}</p>}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-2 mt-2">
          <button type="button" onClick={() => setTarget(null)} className="bg-white border-2 border-sky-200 rounded-full px-5 py-2.5 font-bold text-blue-900" data-testid="exit-guard-stay">{tx.stay}</button>
          <button type="button" onClick={go} disabled={!!target?.q && !answer} className="glossy-btn bg-yellow-300 text-blue-950 font-fredoka font-bold px-6 py-2.5 rounded-full disabled:opacity-60" data-testid="exit-guard-continue">{tx.go}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
